/**
 * Base64 Text Encoding/Decoding Service
 * Implements business logic for Base64 text operations
 */

import { EncodingOperation, EncoderToolType, CharacterEncoding } from '../types';
import { ErrorHandler, ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export class Base64TextService {
  /**
   * Encode text to Base64
   */
  static async encode(
    text: string,
    encoding: CharacterEncoding = CharacterEncoding.UTF8
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(text, 
        CommonValidator.getValidationRules(EncoderToolType.BASE64_TEXT)
      );
      
      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.BASE64_TEXT, validation.suggestion);
      }

      // Perform encoding
      const encodedText = this.encodeText(text, encoding);
      
      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.BASE64_TEXT,
        operationType: 'encode',
        inputData: text,
        outputData: encodedText,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          encoding,
          originalLength: text.length,
          encodedLength: encodedText.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }
      
      throw new ProcessingError(
        'Failed to encode text to Base64',
        EncoderToolType.BASE64_TEXT,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Decode Base64 to text
   */
  static async decode(
    base64Text: string,
    encoding: CharacterEncoding = CharacterEncoding.UTF8
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(base64Text, 
        CommonValidator.getValidationRules(EncoderToolType.BASE64_TEXT)
      );
      
      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.BASE64_TEXT, validation.suggestion);
      }

      // Additional Base64 format validation
      if (!this.isValidBase64(base64Text)) {
        throw new ValidationError(
          'Invalid Base64 format',
          EncoderToolType.BASE64_TEXT,
          'Ensure the input contains valid Base64 characters only'
        );
      }

      // Perform decoding
      const decodedText = this.decodeText(base64Text, encoding);
      
      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.BASE64_TEXT,
        operationType: 'decode',
        inputData: base64Text,
        outputData: decodedText,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          encoding,
          originalLength: base64Text.length,
          decodedLength: decodedText.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }
      
      throw new ProcessingError(
        'Failed to decode Base64 text',
        EncoderToolType.BASE64_TEXT,
        'Please check your Base64 input and try again'
      );
    }
  }

  /**
   * Real-time encoding with debouncing support
   */
  static async encodeRealTime(
    text: string,
    encoding: CharacterEncoding = CharacterEncoding.UTF8,
    debounceMs: number = 300
  ): Promise<EncodingOperation> {
    return new Promise((resolve) => {
      // Simple debounce implementation
      setTimeout(async () => {
        try {
          const result = await this.encode(text, encoding);
          resolve(result);
        } catch (error) {
          resolve({
            id: this.generateOperationId(),
            toolType: EncoderToolType.BASE64_TEXT,
            operationType: 'encode',
            inputData: text,
            timestamp: new Date(),
            status: 'error',
            errorMessage: ErrorHandler.handle(error, EncoderToolType.BASE64_TEXT)
          });
        }
      }, debounceMs);
    });
  }

  /**
   * Real-time decoding with debouncing support
   */
  static async decodeRealTime(
    base64Text: string,
    encoding: CharacterEncoding = CharacterEncoding.UTF8,
    debounceMs: number = 300
  ): Promise<EncodingOperation> {
    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          const result = await this.decode(base64Text, encoding);
          resolve(result);
        } catch (error) {
          resolve({
            id: this.generateOperationId(),
            toolType: EncoderToolType.BASE64_TEXT,
            operationType: 'decode',
            inputData: base64Text,
            timestamp: new Date(),
            status: 'error',
            errorMessage: ErrorHandler.handle(error, EncoderToolType.BASE64_TEXT)
          });
        }
      }, debounceMs);
    });
  }

  /**
   * Core text encoding implementation
   */
  private static encodeText(text: string, encoding: CharacterEncoding): string {
    switch (encoding) {
      case CharacterEncoding.UTF8:
        return btoa(unescape(encodeURIComponent(text)));
      case CharacterEncoding.ASCII:
        return btoa(text);
      case CharacterEncoding.UTF16:
        // For UTF-16, we need to handle the encoding properly
        const encoder = new TextEncoder();
        const encoded = encoder.encode(text);
        return btoa(String.fromCharCode(...encoded));
      case CharacterEncoding.LATIN1:
        return btoa(unescape(encodeURIComponent(text)));
      default:
        return btoa(unescape(encodeURIComponent(text)));
    }
  }

  /**
   * Core text decoding implementation
   */
  private static decodeText(base64Text: string, encoding: CharacterEncoding): string {
    try {
      switch (encoding) {
        case CharacterEncoding.UTF8:
          return decodeURIComponent(escape(atob(base64Text)));
        case CharacterEncoding.ASCII:
          return atob(base64Text);
        case CharacterEncoding.UTF16:
          const decoded = atob(base64Text);
          const decoder = new TextDecoder('utf-16');
          return decoder.decode(new Uint8Array(decoded.split('').map(char => char.charCodeAt(0))));
        case CharacterEncoding.LATIN1:
          return decodeURIComponent(escape(atob(base64Text)));
        default:
          return decodeURIComponent(escape(atob(base64Text)));
      }
    } catch (error) {
      throw new ValidationError(
        'Failed to decode Base64 text',
        EncoderToolType.BASE64_TEXT,
        'The Base64 input may be corrupted or in an unexpected format'
      );
    }
  }

  /**
   * Validate Base64 format
   */
  private static isValidBase64(text: string): boolean {
    try {
      // Check if the string is properly Base64 encoded
      return btoa(atob(text)) === text;
    } catch {
      return false;
    }
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `base64-text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get encoding info for display
   */
  static getEncodingInfo(encoding: CharacterEncoding): { name: string; description: string } {
    switch (encoding) {
      case CharacterEncoding.UTF8:
        return {
          name: 'UTF-8',
          description: 'Unicode variable-width encoding, supports all characters'
        };
      case CharacterEncoding.ASCII:
        return {
          name: 'ASCII',
          description: 'Basic Latin alphabet, numbers, and symbols'
        };
      case CharacterEncoding.UTF16:
        return {
          name: 'UTF-16',
          description: '16-bit Unicode encoding, efficient for Asian languages'
        };
      case CharacterEncoding.LATIN1:
        return {
          name: 'Latin-1',
          description: 'Western European character set'
        };
      default:
        return {
          name: 'UTF-8',
          description: 'Unicode variable-width encoding, supports all characters'
        };
    }
  }

  /**
   * Batch process multiple texts
   */
  static async batchEncode(
    texts: string[],
    encoding: CharacterEncoding = CharacterEncoding.UTF8
  ): Promise<EncodingOperation[]> {
    const results: EncodingOperation[] = [];
    
    for (const text of texts) {
      try {
        const result = await this.encode(text, encoding);
        results.push(result);
      } catch (error) {
        results.push({
          id: this.generateOperationId(),
          toolType: EncoderToolType.BASE64_TEXT,
          operationType: 'encode',
          inputData: text,
          timestamp: new Date(),
          status: 'error',
          errorMessage: ErrorHandler.handle(error, EncoderToolType.BASE64_TEXT)
        });
      }
    }
    
    return results;
  }

  /**
   * Batch decode multiple Base64 strings
   */
  static async batchDecode(
    base64Texts: string[],
    encoding: CharacterEncoding = CharacterEncoding.UTF8
  ): Promise<EncodingOperation[]> {
    const results: EncodingOperation[] = [];
    
    for (const base64Text of base64Texts) {
      try {
        const result = await this.decode(base64Text, encoding);
        results.push(result);
      } catch (error) {
        results.push({
          id: this.generateOperationId(),
          toolType: EncoderToolType.BASE64_TEXT,
          operationType: 'decode',
          inputData: base64Text,
          timestamp: new Date(),
          status: 'error',
          errorMessage: ErrorHandler.handle(error, EncoderToolType.BASE64_TEXT)
        });
      }
    }

    return results;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}